import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import noop from 'lodash/noop'
import { path } from 'ramda'
import {
  Alert,
  Box,
  Button,
  Stack,
  Text,
  Columns,
  Column,
  TextLink,
  Divider,
  Tiles,
  Dialog,
  useToast,
  IconCopy
} from '@machobear/design-system'
import { useToaster } from '@/models/toaster/hooks'
import useCurrentSession from '@/hooks/use-current-session'
import { select } from '@/store'
import { colors, breakpoints } from '@/styles'
import { copyAsyncText } from '@/utils/copy-to-clipboard'
import {
  CraigslistLogo,
  KijijiLogo,
  FacebookLogo,
  TextMessageIcon,
  EmailIcon
} from '@/components/icons'
import config from '@/config'
import useMediaQuery from '@/hooks/use-media-query'
import CopyListingKijiji from '../utils/copy-listing-kijiji'
import CopyListingFacebook from '../utils/copy-listing-facebook'
import CopyListingCraigslist from '../utils/copy-listing-craigslist'

const { WEB_URL } = config

const ShareListingMain = memo(({ listingId, open, onClose = noop, showModal }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const session = useCurrentSession()
  const toaster = useToaster()
  const showToast = useToast()
  const [copyLoading, setCopyLoading] = useState(false)
  const state = path(['tasks', 'publish_listing', 'steps', 'share', 'state'])(toaster)
  const updateState = toaster.updateState
  useEffect(() => {
    if (state !== 'incomplete') return
    updateState('publish_listing.share', {
      step: {
        state: 'complete'
      }
    })
  }, [updateState, state])
  const copyURL = `${WEB_URL}/rental-listings/${listingId}`

  const data = useSelector(state =>
    select.listing.editListing(state, {
      listingId
    })
  )

  const listing =
    useSelector(state =>
      select.listing.listingDetail(state, {
        listingId: listingId
      })
    ) || {}

  const listingPublished = listing.listings?.state_machine === 'published'

  const handleClose = async () => {
    onClose()
  }

  const DATA = {
    craigslist: CopyListingCraigslist,
    kijiji: CopyListingKijiji,
    facebook: CopyListingFacebook,
    url: dispatch.listing.generateDeeplink
  }

  const copyToClipboard = async type => {
    const promise = DATA[type](data, session.setting_size_unit)
    await copyAsyncText(promise)
  }

  const copyUrlToClipboard = async () => {
    const promise = DATA['url'](data, session.setting_size_unit)
    setCopyLoading(true)
    await copyAsyncText(promise)
    setCopyLoading(false)
    showToast({
      message: t(
        'l.listing.share_copy.toast_success.text',
        'The listing link has been copied to your clipboard!'
      ),
      tone: 'positive'
    })
  }

  const handleShare = async type => {
    if (DATA[type]) {
      if (type === 'url') await copyUrlToClipboard()
      else await copyToClipboard(type)
    }
  }

  return (
    <>
      <Dialog
        id="share-listing-main-dialog"
        title={t('l.listing.view.share.title', 'Share Your Listing')}
        open={open}
        onClose={handleClose}
      >
        <Stack space={isMobile ? 'gutter' : 'large'}>
          {!listingPublished && (
            <Alert tone="caution">
              <Text size="small">
                {t(
                  'l.listing.share.warning.text',
                  'This listing is still pending verification and is only viewable to you.'
                )}
              </Text>
            </Alert>
          )}
          <Stack space={isMobile ? 'medium' : 'gutter'}>
            <Text size="large" weight="medium">
              {t('l.listing.share.post_to_other_platforms.text', 'Post to other platforms')}
            </Text>
            <Tiles space="xsmall" columns={{ mobile: 1, tablet: 3, desktop: 3 }}>
              <Button
                id="app_share_click_craigslist"
                size="small"
                variant="ghost"
                onClick={() => {
                  handleShare('craigslist')
                  showModal('craigslistDialog')()
                  onClose()
                }}
              >
                <Box display="flex" flexDirection="row" gap="xxsmall" alignItems="center">
                  <CraigslistLogo />
                  Craigslist
                </Box>
              </Button>

              <Button
                id="app_share_click_kijiji"
                size="small"
                variant="ghost"
                onClick={() => {
                  handleShare('kijiji')
                  showModal('kijijiDialog')()
                  onClose()
                }}
              >
                <Box display="flex" flexDirection="row" gap="xxsmall" alignItems="center">
                  <KijijiLogo />
                  Kijiji
                </Box>
              </Button>

              <Button
                id="app_share_click_fb"
                size="small"
                variant="ghost"
                onClick={() => {
                  handleShare('facebook')
                  showModal('facebookDialog')()
                  onClose()
                }}
              >
                <Box display="flex" flexDirection="row" gap="xxsmall" alignItems="center">
                  <FacebookLogo />
                  Facebook
                </Box>
              </Button>
            </Tiles>
            <Alert tone="promote">
              <Text size="small">
                {t('l.listing.share.short_on_time.text', 'Short on time? Let us do the work.')}{' '}
                <TextLink
                  id="app_share_click_anchor_multiadv"
                  href={`/usage-credits/products/rental-advertising`}
                  target="_blank"
                  weight="regular"
                  size="small"
                >
                  {t('l.listing.share.explore.link.text', 'Explore Multi-Platform Advertising')}
                </TextLink>
              </Text>
            </Alert>
          </Stack>
          <Divider />
          <Stack space="medium">
            <Text size="large" weight="medium">
              {t('l.listing.share.invite_others.text', 'Invite others')}
            </Text>
            <Box
              paddingY="xsmall"
              paddingX="small"
              style={{ backgroundColor: `${colors.grey96}` }}
              borderRadius="small"
            >
              <Box
                display="flex"
                flexDirection={{ mobile: 'column', tablet: 'row', desktop: 'row' }}
                justifyContent={{
                  mobile: 'center',
                  tablet: 'spaceBetween',
                  desktop: 'spaceBetween'
                }}
                alignItems="center"
                gap={'medium'}
              >
                <Box width="full">
                  <Text size="small" maxLines={1}>
                    {' '}
                    {copyURL}
                  </Text>
                </Box>

                <Box style={{ width: isMobile ? '100%' : 175 }}>
                  <Button
                    id="app_share_click_copy"
                    size="standard"
                    variant="ghost"
                    iconPosition="leading"
                    icon={<IconCopy />}
                    onClick={() => handleShare('url')}
                    loading={copyLoading}
                  >
                    {t('listing.view.copy.button', 'Copy')}
                  </Button>
                </Box>
              </Box>
            </Box>
            <Columns space="xsmall">
              <Column width="1/2">
                <Button
                  id="app_share_click_text"
                  size="standard"
                  variant="ghost"
                  iconPosition="leading"
                  onClick={() => {
                    showModal('phoneDialog')()
                    onClose()
                  }}
                >
                  <Box display="flex" flexDirection="row" gap="xxsmall" alignItems="center">
                    <TextMessageIcon />
                    {t('l.listing.share.text_message.button.text', 'Text')}
                  </Box>
                </Button>
              </Column>
              <Column width="1/2">
                <Button
                  id="app_share_click_email"
                  size="standard"
                  variant="ghost"
                  iconPosition="leading"
                  onClick={() => {
                    showModal('emailDialog')()
                    onClose()
                  }}
                >
                  <Box display="flex" flexDirection="row" gap="xxsmall" alignItems="center">
                    <EmailIcon />
                    {t('l.listing.share.email.button.text', 'Email')}
                  </Box>
                </Button>
              </Column>
            </Columns>
          </Stack>
        </Stack>
      </Dialog>
    </>
  )
})

export default ShareListingMain
